import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tv, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import TVRemote from "./tv/TVRemote";
import TVScenes from "./tv/TVScenes";
import TVAutomations from "./tv/TVAutomations";
import TVConnection from "./tv/TVConnection";

const TABS = [
  { key: "fernbedienung", label: "Fernbedienung" },
  { key: "kinomodus", label: "Kinomodus" },
  { key: "automationen", label: "Automationen" },
  { key: "verbindung", label: "Verbindung" },
];

export const errMsg = (e, fb = "Fehler") => e?.response?.data?.detail || fb;

export default function TVControl() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "fernbedienung";
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tv/status");
      setStatus(data);
      setStatusErr("");
    } catch (e) {
      setStatusErr(errMsg(e, "Status nicht abrufbar"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const o = params.get("oauth");
    if (o === "ok") { toast.success("SmartThings verbunden"); }
    if (o === "error") { toast.error("SmartThings-Verbindung fehlgeschlagen"); }
  }, [params]);

  const power = status?.power;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="tv-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Tv size={22} className="text-[#D4AF37]" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Steuerung</div>
          <h1 className="font-heading text-3xl">TV-Steuerung</h1>
        </div>
        <button onClick={refresh} disabled={loading} data-testid="tv-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-40">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Aktualisieren
        </button>
      </div>

      <div className="card-lux p-4 grid grid-cols-3 gap-3 text-center" data-testid="tv-status-bar">
        <StatusCell label="Status" testid="tv-status-power"
          value={statusErr ? "—" : power === "on" ? "An" : power === "off" ? "Aus" : "—"}
          accent={power === "on" ? "text-[#7FD49A]" : "text-[#A8A398]"} />
        <StatusCell label="Quelle" value={status?.input || "—"} testid="tv-status-input" />
        <StatusCell label="Lautstärke" testid="tv-status-volume"
          value={status?.volume != null ? `${status.volume}${status.mute === "muted" ? " · stumm" : ""}` : "—"} />
        {statusErr && <div className="col-span-3 text-xs text-[#EE9CA1]" data-testid="tv-status-error">{statusErr}</div>}
      </div>

      <div className="flex gap-1 border-b border-[#1A1A1A] overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setParams({ tab: t.key })} data-testid={`tv-tab-${t.key}`}
            className={`px-3 sm:px-4 py-2 whitespace-nowrap text-[10px] uppercase tracking-[0.25em] border-b-2 ${tab === t.key ? "border-[#D4AF37] text-[#EEDC82]" : "border-transparent text-[#A8A398]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "fernbedienung" && <TVRemote status={status} onChanged={refresh} />}
      {tab === "kinomodus" && <TVScenes onChanged={refresh} />}
      {tab === "automationen" && <TVAutomations />}
      {tab === "verbindung" && <TVConnection onChanged={refresh} />}
    </div>
  );
}

function StatusCell({ label, value, accent = "text-[#F5F0E6]", testid }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#A8A398]">{label}</div>
      <div className={`font-heading text-lg mt-1 ${accent}`} data-testid={testid}>{value}</div>
    </div>
  );
}
